import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { CheckInService } from '../services/checkInService';
import { CheckInState, CheckInResult, CheckInData } from '../types';

export function useCheckIn(displayId: number | null) {
  const { user } = useAuth();
  const [state, setState] = useState<CheckInState>({
    hasCheckedIn: false,
    todayCheckedIn: false,
    totalCheckIns: 0,
    uniqueVisitors: 0,
    userCheckIns: 0,
    loading: false,
  });

  // Fetch initial check-in state
  useEffect(() => {
    if (!displayId) return;

    const fetchCheckInState = async () => {
      setState(prev => ({ ...prev, loading: true }));

      try {
        const status = await CheckInService.getCheckInStatus(displayId);

        setState({
          hasCheckedIn: status.has_checked_in,
          todayCheckedIn: status.today_checked_in,
          totalCheckIns: status.total_checkins,
          uniqueVisitors: status.unique_visitors,
          userCheckIns: status.user_checkins,
          lastCheckIn: status.last_checkin,
          loading: false,
        });
      } catch (error) {
        console.error('Error fetching check-in state:', error);
        setState(prev => ({ ...prev, loading: false, error: 'Failed to load check-in status' }));
      }
    };

    fetchCheckInState();
  }, [displayId, user]);

  const checkIn = async (data?: CheckInData): Promise<CheckInResult> => {
    if (!user) {
      return { success: false, requiresAuth: true };
    }

    if (!displayId || state.loading) {
      return { success: false, error: 'Invalid display or loading' };
    }

    if (state.todayCheckedIn) {
      toast.error('You\'ve already checked in here today!');
      return { success: false, error: 'Already checked in today' };
    }

    setState(prev => ({ ...prev, loading: true }));

    try {
      // Get user location if available
      const location = await CheckInService.getCurrentLocation();

      // Optimistic update
      setState(prev => ({
        ...prev,
        todayCheckedIn: true,
        hasCheckedIn: true,
        totalCheckIns: prev.totalCheckIns + 1,
        userCheckIns: prev.userCheckIns + 1,
        uniqueVisitors: prev.hasCheckedIn ? prev.uniqueVisitors : prev.uniqueVisitors + 1,
      }));

      const result = await CheckInService.checkIn(displayId, data, location || undefined);

      if (result.success) {
        // Update with server response
        setState(prev => ({
          ...prev,
          loading: false,
          totalCheckIns: result.total_checkins || prev.totalCheckIns,
        }));

        // Show success message
        if (result.location_verified) {
          toast.success('✓ Checked in successfully! Location verified.', {
            description: result.is_first_checkin ? 'First time visiting this display!' : undefined,
          });
        } else {
          toast.success('✓ Checked in successfully!', {
            description: result.is_first_checkin ? 'First time visiting this display!' : undefined,
          });
        }

        // Show achievement notifications
        if (result.is_first_checkin) {
          setTimeout(() => {
            toast.success('🎉 Achievement Unlocked: First Check-in!');
          }, 1000);
        }

        if (result.total_checkins === 10) {
          setTimeout(() => {
            toast.success('🎉 Achievement Unlocked: Explorer (10 check-ins)!');
          }, 1500);
        }

        return result;
      } else {
        // Revert optimistic update
        setState(prev => ({
          ...prev,
          todayCheckedIn: false,
          hasCheckedIn: prev.userCheckIns > 1,
          totalCheckIns: Math.max(0, prev.totalCheckIns - 1),
          userCheckIns: Math.max(0, prev.userCheckIns - 1),
          uniqueVisitors: prev.hasCheckedIn ? prev.uniqueVisitors : Math.max(0, prev.uniqueVisitors - 1),
          loading: false,
          error: result.error,
        }));

        if (result.error?.includes('Already checked in')) {
          toast.error('You\'ve already checked in here today!');
        } else {
          toast.error('Check-in failed. Please try again.');
        }

        return result;
      }
    } catch (error) {
      console.error('Check-in error:', error);

      // Revert optimistic update
      setState(prev => ({
        ...prev,
        todayCheckedIn: false,
        hasCheckedIn: prev.userCheckIns > 1,
        totalCheckIns: Math.max(0, prev.totalCheckIns - 1),
        userCheckIns: Math.max(0, prev.userCheckIns - 1),
        uniqueVisitors: prev.hasCheckedIn ? prev.uniqueVisitors : Math.max(0, prev.uniqueVisitors - 1),
        loading: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      }));

      toast.error('Check-in failed. Please try again.');
      return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
    }
  };

  const refresh = async () => {
    if (!displayId) return;

    try {
      setState(prev => ({ ...prev, loading: true }));
      const status = await CheckInService.getCheckInStatus(displayId);

      setState(prev => ({
        ...prev,
        hasCheckedIn: status.has_checked_in,
        todayCheckedIn: status.today_checked_in,
        totalCheckIns: status.total_checkins,
        uniqueVisitors: status.unique_visitors,
        userCheckIns: status.user_checkins,
        lastCheckIn: status.last_checkin,
        loading: false,
        error: undefined,
      }));
    } catch (error) {
      console.error('Error refreshing check-in state:', error);
      setState(prev => ({
        ...prev,
        loading: false,
        error: 'Failed to refresh check-in status'
      }));
    }
  };

  return {
    ...state,
    checkIn,
    refresh,
    canCheckIn: user && !state.todayCheckedIn && !state.loading,
  };
}

// Hook for batch fetching check-in states
export function useBatchCheckInStatuses(displayIds: number[]) {
  const { user } = useAuth();
  const [states, setStates] = useState<Record<number, CheckInState>>({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!displayIds.length) return;

    const fetchBatchStates = async () => {
      setLoading(true);
      try {
        const statuses = await CheckInService.getBatchCheckInStatuses(displayIds);

        const newStates: Record<number, CheckInState> = {};
        Object.entries(statuses).forEach(([displayId, status]) => {
          newStates[parseInt(displayId)] = {
            hasCheckedIn: status.has_checked_in,
            todayCheckedIn: status.today_checked_in,
            totalCheckIns: status.total_checkins,
            uniqueVisitors: status.unique_visitors,
            userCheckIns: status.user_checkins,
            lastCheckIn: status.last_checkin,
            loading: false,
          };
        });

        setStates(newStates);
      } catch (error) {
        console.error('Error fetching batch check-in states:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchBatchStates();
  }, [displayIds, user]);

  return { states, loading };
}